import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  drawRaffle,
  fetchRaffleStatus,
  BkfcRaffleStatus,
} from "../bkfc/api";

// The raffle page, only reachable through the emailed magic link
// (?uid=...&token=...). Figma screens: "Your reward is waiting" →
// "Drawing your reward" → "You received reward X" (or the no-reward copy).
// One raffle per certificate; the result is final.
type Phase = "loading" | "invalid" | "ready" | "drawing" | "result";

interface RaffleResult {
  won: boolean;
  rewardName: string | null;
}

const BkfcRaffle = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawUid = searchParams.get("uid");
  const token = searchParams.get("token");
  const uid =
    rawUid && /^[A-F0-9]+$/i.test(rawUid) ? rawUid.toUpperCase() : null;

  const [phase, setPhase] = useState<Phase>("loading");
  const [invalidReason, setInvalidReason] = useState<string>(
    "This raffle link is not valid.",
  );
  const [email, setEmail] = useState<string | null>(null);
  const [result, setResult] = useState<RaffleResult | null>(null);

  useEffect(() => {
    let isMounted = true;

    const check = async () => {
      if (!uid || !token) {
        setPhase("invalid");
        return;
      }

      try {
        const status: BkfcRaffleStatus = await fetchRaffleStatus(uid, token);
        if (!isMounted) return;

        if (!status.valid) {
          setInvalidReason(
            status.reason === "expired_token"
              ? "This raffle link has expired. Request a new one from My certificates."
              : "This raffle link is not valid. If you requested a new link, use the most recent email.",
          );
          setPhase("invalid");
          return;
        }

        setEmail(status.email ?? null);

        if (status.drawn) {
          setResult({
            won: Boolean(status.won),
            rewardName: status.rewardName ?? null,
          });
          setPhase("result");
        } else {
          setPhase("ready");
        }
      } catch {
        if (!isMounted) return;
        setInvalidReason(
          "Could not check this raffle link. Please try again shortly.",
        );
        setPhase("invalid");
      }
    };

    check();
    return () => {
      isMounted = false;
    };
  }, [uid, token]);

  const handleReveal = async () => {
    if (!uid || !token || phase !== "ready") return;
    setPhase("drawing");
    try {
      const res = await drawRaffle(uid, token);
      setResult({ won: res.won, rewardName: res.rewardName });
      setPhase("result");
    } catch (err) {
      setInvalidReason(
        err instanceof Error ? err.message : "Failed to run the raffle",
      );
      setPhase("invalid");
    }
  };

  return (
    <div className="px-6 pt-8 pb-6 flex-1 flex flex-col">
      {phase === "loading" && (
        <div className="flex-1 flex flex-col items-center justify-center">
          <img
            src="/images/bkfc/loading-badge.png"
            alt="Loading"
            className="w-20 animate-pulse"
          />
          <p className="mt-6 text-base text-[#626262] text-center">
            Checking your raffle link...
          </p>
        </div>
      )}

      {phase === "invalid" && (
        <div className="flex-1 flex flex-col items-center justify-center">
          <img
            src="/images/bkfc/error-badge.png"
            alt="Error"
            className="w-[70px] h-[70px]"
          />
          <h1 className="mt-6 text-2xl font-bold text-center">
            Link not valid
          </h1>
          <p className="mt-3 text-base text-[#626262] text-center">
            {invalidReason}
          </p>
          <button
            onClick={() => navigate("/rewards/bkfc")}
            className="mt-6 w-full h-[46px] rounded-[80px] border-2 border-black text-black font-bold text-base transition-colors hover:bg-gray-100"
          >
            My certificates
          </button>
        </div>
      )}

      {phase === "ready" && (
        <>
          <p className="text-sm text-[#626262] text-center tracking-[2.8px] uppercase">
            BKFC Raffle
          </p>

          <h1 className="mt-6 text-2xl font-bold text-center">
            Your reward is waiting
          </h1>

          <p className="mt-6 text-base text-[#626262] text-center">
            One reveal for this certificate. The result is final
          </p>

          <div className="mt-6 w-full">
            {/* Figma 3686:1237 update: photographic wrapped-package-in-ring
                artwork replaced the old beige "?" svg */}
            <img
              src="/images/bkfc/mystery-card.jpg"
              alt="Mystery reward"
              className="w-full h-[264px] object-cover rounded-[22px]"
            />
          </div>

          <p className="mt-3 text-sm text-[#626262] text-center break-all">
            {uid}
          </p>

          <button
            onClick={handleReveal}
            className="mt-6 w-full h-[46px] rounded-[80px] bg-[#FFD43E] text-black font-bold text-base transition-colors hover:bg-[#f5c822]"
          >
            Reveal reward
          </button>
        </>
      )}

      {phase === "drawing" && (
        <div className="flex-1 flex flex-col items-center justify-center">
          <img
            src="/images/bkfc/loading-badge.png"
            alt="Loading"
            className="w-20 animate-pulse"
          />
          <h1 className="mt-6 text-2xl font-bold text-center">
            Drawing your reward
          </h1>
          <p className="mt-3 text-base text-[#626262] text-center">
            One moment
          </p>
        </div>
      )}

      {phase === "result" && result && (
        <div className="flex-1 flex flex-col items-center justify-center">
          <p className="text-sm text-[#626262] text-center tracking-[2.8px] uppercase">
            {result.won ? "Congratulations" : "BKFC Raffle"}
          </p>

          {/* No image on the result screen per the Figma design update
              (2026-08-26): the reward name in the heading is the reveal */}
          <h1 className="mt-6 text-2xl font-bold text-center">
            {result.won
              ? `You received ${result.rewardName ?? "a reward"}`
              : "No reward this time"}
          </h1>

          <p className="mt-6 text-base text-[#626262] text-center">
            {result.won
              ? "The team will be in contact with you for the reward delivery"
              : "Unfortunately you haven't received a reward — better luck next time. Your certificate remains yours forever."}
          </p>

          {email && (
            <p className="mt-3 text-sm text-[#626262] text-center">
              Confirmation sent to {email}
            </p>
          )}

          <button
            onClick={() => navigate("/rewards/bkfc")}
            className="mt-6 w-full h-[46px] rounded-[80px] border-2 border-black text-black font-bold text-base transition-colors hover:bg-gray-100"
          >
            My certificates
          </button>
        </div>
      )}
    </div>
  );
};

export default BkfcRaffle;
